import isNil from 'lodash/isNil';
import isString from 'lodash/isString';
import isEmpty from 'lodash/isEmpty';
import { APP_LANGUAGE } from 'commons/models/common';
import { EMPTY_OBJECT, EMPTY_STRING } from 'commons-ui/src/constants';
import { INoticeBoard } from '../interfaces';
import { ELaunchWidgetType } from './constants';

type INoticeEditBoard = INoticeBoard & { index?: number };

export interface INoticeBoardCommonRow {
	_id: string;
	maxAppVersion: number;
	minAppVersion: number;
	minGames: number;
	version: number;
	isArchived: boolean;
	exclusiveSegments: string;
	inclusiveSegments: string;
	abExperiments?: { [key: string]: Array<string> };
	priority: number;
	lifetime: number;
	expiry: number;
	validFrom: number;
	validTill: number;
	repeat: number;
	isCrossable: boolean;
	enabled: boolean;
	type: 'BACKEND' | 'MANUAL' | 'ADHOC' | 'ADHOC_ONCE';
	name: string;
	cta?: INoticeCTA;
	ctaType: string;
	ctaValue: string;
	ctaSelectValue: string;
	cardBgColor: string;
	cardBgUrl: string;
	id: string;
	index: number;
}

export interface INoticeBoardRow extends INoticeBoardCommonRow {
	imageUrl: string;
	subtitleText: TLanguageText | string;
	subtitleColor: string;
	titleText: TLanguageText | string;
	titleColor: string;
}

export interface ILauchWidgetRow extends INoticeBoardCommonRow {
	videoDuration: number;
	videoUrl: string;
	ctaDisplayText: TLanguageText | string;
	launchWidgetType: ELaunchWidgetType;
}

interface INoticeCTA {
	type: string;
	value: string;
	displayText?: TLanguageText;
	params?: {
		gameName?: string;
	};
}

export interface INoticeCommonSubmitData {
	name: string;
	type: string;
	enabled: boolean;
	cardBg: {
		url?: string;
		color?: string;
	};
	version: number;
	isArchived: boolean;
	isCrossable: boolean;
	cta?: INoticeCTA;
	minAppVersion: number;
	maxAppVersion: number;
	expiry: number;
	validFrom: number;
	validTill: number;
	repeat: number;
	lifetime: number;
	priority: number;
	inclusiveSegments: string;
	exclusiveSegments: string;
	abExperiments: string;
	minGames: number;
}

export type TLanguageText = { [key in APP_LANGUAGE]?: string };
export interface INoticeSubmitData extends INoticeCommonSubmitData {
	title: {
		text: string | TLanguageText;
		color: string;
	};
	subtitle: {
		text: string | TLanguageText;
		color: string;
	};
	imageUrl: string;
}

export interface ILaunchWidgetSubmitData extends INoticeCommonSubmitData {
	launchWidgetType: ELaunchWidgetType;
	videoMeta: {
		url: string;
		duration: number;
	};
}

const getTitleAndSubTitleText = (
	titleText: TLanguageText | string,
	subtitleText: TLanguageText | string
): {
	titleTextUpdated: TLanguageText | string;
	subTitleTextUpdated: TLanguageText | string;
} => {
	let subTitleTextUpdated: TLanguageText | string = subtitleText;
	if (isString(subtitleText)) {
		subTitleTextUpdated = {
			[APP_LANGUAGE.ENGLISH]: subtitleText
		};
	}
	let titleTextUpdated: TLanguageText | string = titleText;
	if (isString(titleText)) {
		titleTextUpdated = {
			[APP_LANGUAGE.ENGLISH]: titleText
		};
	}

	return { titleTextUpdated, subTitleTextUpdated };
};

const formatAbExpsToString = (abExperiments: { [key: string]: Array<string> } | undefined): string => {
	if (isEmpty(abExperiments)) {
		return EMPTY_STRING;
	}
	let abExpsString: string = '';
	Object.keys(abExperiments).forEach((abExpName: string) => {
		abExperiments[abExpName].forEach((bucket: string) => {
			abExpsString += `${abExpName}-${bucket},`;
		});
	});
	return !isEmpty(abExpsString) ? abExpsString.substring(0, abExpsString.length - 1) : EMPTY_STRING;
};

const hydrateFormData = (data: INoticeBoardCommonRow): INoticeCommonSubmitData => {
	const { cardBgColor, cardBgUrl, abExperiments } = data;

	const hydratedData: INoticeCommonSubmitData = {
		maxAppVersion: data.maxAppVersion,
		minAppVersion: data.minAppVersion,
		minGames: data.minGames,
		exclusiveSegments: data.exclusiveSegments,
		inclusiveSegments: data.inclusiveSegments,
		priority: data.priority,
		lifetime: data.lifetime,
		expiry: data.expiry,
		repeat: data.repeat,
		isCrossable: data.isCrossable,
		enabled: data.enabled,
		type: data.type,
		name: data.name,
		cardBg: {
			color: cardBgColor,
			url: cardBgUrl
		},
		isArchived: data.isArchived || false,
		version: data.version,
		validFrom: data.validFrom,
		validTill: data.validTill,
		abExperiments: formatAbExpsToString(abExperiments)
	};

	return hydratedData;
};

export const hydrateNoticeFormData = (data: INoticeBoardRow): INoticeSubmitData => {
	const { ctaValue, ctaType, titleText, subtitleText, titleColor, subtitleColor, imageUrl } = data;
	const { titleTextUpdated, subTitleTextUpdated } = getTitleAndSubTitleText(titleText, subtitleText);
	const hydratedData: INoticeCommonSubmitData = hydrateFormData(data);
	const hydratedNoticeData: INoticeSubmitData = {
		...hydratedData,
		cta: {
			type: ctaType,
			value: ctaValue
		},
		title: {
			text: titleTextUpdated,
			color: titleColor
		},
		subtitle: {
			text: subTitleTextUpdated,
			color: subtitleColor
		},
		imageUrl
	};

	return hydratedNoticeData;
};

const getLaunchWidgetCTA = (data: ILauchWidgetRow): INoticeCTA | undefined => {
	const { cta } = data;
	if (isEmpty(cta)) {
		return undefined;
	}
	if (cta?.params?.gameName) {
		cta.params.gameName = cta.params.gameName.toLowerCase();
	}

	return cta;
};

export const hydrateLaunchWidgetFormData = (data: ILauchWidgetRow): ILaunchWidgetSubmitData => {
	const { launchWidgetType, videoUrl, videoDuration } = data;

	const hydratedData: INoticeCommonSubmitData = hydrateFormData(data);
	const hydratedLaunchWidgetData: ILaunchWidgetSubmitData = {
		...hydratedData,
		launchWidgetType,
		videoMeta: {
			url: videoUrl,
			duration: videoDuration
		},
		cta: getLaunchWidgetCTA(data)
	};
	return hydratedLaunchWidgetData;
};

const getText = (text: string | TLanguageText): string | TLanguageText => {
	if (isString(text)) {
		return text;
	}
	return Object.entries(text).reduce((acc: TLanguageText, [language, value]: [string, string]) => {
		const lang: APP_LANGUAGE = language as APP_LANGUAGE;
		if (isEmpty(value)) return acc;
		return { ...acc, [lang]: value };
	}, {} as TLanguageText);
};

const checkIfLaunchWidgetData = (formData: INoticeSubmitData | ILaunchWidgetSubmitData): boolean => {
	return (formData as ILaunchWidgetSubmitData).launchWidgetType in ELaunchWidgetType;
};

const getNoticeBoardData = (formData: INoticeSubmitData | ILaunchWidgetSubmitData): Partial<INoticeSubmitData> => {
	if (checkIfLaunchWidgetData(formData) === true) {
		return {};
	}
	const newformData: INoticeSubmitData = formData as INoticeSubmitData;
	return {
		title: {
			text: getText(newformData.title.text),
			color: newformData.title.color
		},
		subtitle: {
			text: getText(newformData.subtitle.text),
			color: newformData.subtitle.color
		},
		imageUrl: newformData.imageUrl
	};
};

const formatAbExps = (abExps: string): { [key: string]: Array<string> } => {
	if (isEmpty(abExps) || (isString(abExps) && abExps.trim().length === 0)) {
		return EMPTY_OBJECT;
	}
	const expsBucket: { [key: string]: Array<string> } = {};
	abExps.split(',').forEach((expAndBucket: string) => {
		const expAndBucketArr: string[] = expAndBucket.split('-');
		if (expsBucket[expAndBucketArr[0]]) {
			expsBucket[expAndBucketArr[0]].push(expAndBucketArr[1]);
		} else {
			expsBucket[expAndBucketArr[0]] = [expAndBucketArr[1]];
		}
	});
	return expsBucket;
};

const getLaunchWidgetData = (formData: INoticeSubmitData | ILaunchWidgetSubmitData): Partial<ILaunchWidgetSubmitData> => {
	if (checkIfLaunchWidgetData(formData) === false) {
		return {};
	}
	const newformData: ILaunchWidgetSubmitData = formData as ILaunchWidgetSubmitData;
	return {
		launchWidgetType: newformData.launchWidgetType,
		videoMeta: {
			url: newformData.videoMeta.url,
			duration: newformData.videoMeta.duration
		}
	};
};

export const dehydrateFormData = (formData: INoticeSubmitData | ILaunchWidgetSubmitData): { noticeData: INoticeBoard } => {
	const noticeBoardData: Partial<INoticeSubmitData> = getNoticeBoardData(formData);
	const launchWidgetData = getLaunchWidgetData(formData);
	const noticeData: INoticeEditBoard = {
		...noticeBoardData,
		...launchWidgetData,
		name: formData.name,
		type: formData.type,
		enabled: formData.enabled,
		cardBg: {
			...(!isEmpty(formData.cardBg.url) && { url: formData.cardBg.url }),
			...(!isEmpty(formData.cardBg.color) && { color: formData.cardBg.color })
		},
		isCrossable: formData.isCrossable,
		isArchived: formData.isArchived,
		version: formData.version,
		minAppVersion: formData.minAppVersion,
		maxAppVersion: formData.maxAppVersion,
		expiry: formData.expiry,
		repeat: formData.repeat,
		lifetime: formData.lifetime,
		priority: formData.priority,
		minGames: formData.minGames,
		inclusiveSegments:
			isNil(formData.inclusiveSegments) || formData.inclusiveSegments.length === 0 ? [] : formData.inclusiveSegments.split(','),
		exclusiveSegments:
			isNil(formData.exclusiveSegments) || formData.exclusiveSegments.length === 0 ? [] : formData.exclusiveSegments.split(','),
		abExperiments: formatAbExps(formData.abExperiments),
		validFrom: Number(formData.validFrom),
		validTill: Number(formData.validTill),
		...(!isEmpty(formData.cta) && {
			cta: {
				type: formData.cta.type,
				value: formData.cta.value,
				params: formData.cta.params,
				displayText: formData.cta.displayText
			}
		})
	};

	const cta: INoticeCTA = JSON.parse(JSON.stringify(noticeData.cta));

	if (!isNil(noticeData.cta)) {
		if (!isNil(cta.params) && isEmpty(cta.params)) {
			delete noticeData.cta.params;
		}
		if (!isNil(cta.displayText) && isEmpty(cta.displayText)) {
			delete noticeData.cta.displayText;
		}
	}
	return { noticeData };
};
